import React, { useState, useMemo } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  Alert,
  AlertTitle,
  Divider,
  Paper,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
  ViewList as ViewListIcon,
  Link as LinkIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Public as PublicIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import {
  TemplateControl,
  TemplateSection,
  ComposerAssetData,
} from '@horwath-htl/shared-types';

/**
 * Props for the PreviewStep component
 */
export interface PreviewStepProps {
  /** The deliverable ID being composed */
  deliverableId: number;
  /** The selected template ID */
  templateId: string;
  /** The template name for display */
  templateName: string;
  /** Template type (global, personal, or shared) */
  templateType: 'global' | 'personal' | 'shared';
  /** All available sections from the template */
  allSections: TemplateSection[];
  /** Selected section IDs for the document */
  selectedSections: string[];
  /** All controls from the template */
  allControls: TemplateControl[];
  /** Asset mappings for controls */
  mappings: ComposerAssetData[];
  /** Callback to navigate to a specific step */
  onNavigateToStep?: (step: number) => void;
  /** Callback when validation state changes */
  onValidationChange?: (isValid: boolean, errors: ValidationError[]) => void;
}

/**
 * Validation error details
 */
export interface ValidationError {
  type: 'error' | 'warning';
  message: string;
  controlId?: string;
  sectionId?: string;
  step?: number;
}

/**
 * PreviewStep Component
 *
 * Step 4 of the DeliverableBuilder workflow.
 * Users review all their selections before generating the document.
 * Displays summary cards for template, sections, and asset mappings,
 * with validation and quick navigation to fix issues.
 */
export const PreviewStep: React.FC<PreviewStepProps> = ({
  deliverableId,
  templateId,
  templateName,
  templateType,
  allSections,
  selectedSections,
  allControls,
  mappings,
  onNavigateToStep,
  onValidationChange,
}) => {
  // Accordion expansion state
  const [expandedPanel, setExpandedPanel] = useState<string | false>('template');

  // Compute validation errors and mapping statistics
  const validationResult = useMemo(() => {
    const errors: ValidationError[] = [];
    const warnings: ValidationError[] = [];

    // Get controls for selected sections (or all if no section filtering)
    const relevantControls = allControls.filter(control => {
      if (!control.section_id) return true; // Controls without sections are always relevant
      return selectedSections.includes(control.section_id);
    });

    // Check for unmapped required controls
    const requiredControls = relevantControls.filter(control => control.required && !control.auto_mapped);
    const mappedControlIds = new Set(
      mappings.filter(m => m.assetId || m.content).map(m => m.controlId)
    );

    requiredControls.forEach(control => {
      if (!mappedControlIds.has(control.id)) {
        errors.push({
          type: 'error',
          message: `Required control "${control.name}" is not mapped`,
          controlId: control.id,
          sectionId: control.section_id || undefined,
          step: 3, // Asset mapping step
        });
      }
    });

    // Check for unmapped optional controls (as warnings)
    const optionalControls = relevantControls.filter(control => !control.required && !control.auto_mapped);
    optionalControls.forEach(control => {
      if (!mappedControlIds.has(control.id)) {
        warnings.push({
          type: 'warning',
          message: `Optional control "${control.name}" is not mapped`,
          controlId: control.id,
          sectionId: control.section_id || undefined,
          step: 3,
        });
      }
    });

    // Warn if no sections are selected (unless template has no sections)
    if (allSections.length > 0 && selectedSections.length === 0) {
      warnings.push({
        type: 'warning',
        message: 'No sections selected - the entire template will be used',
        step: 2,
      });
    }

    const totalMappableControls = relevantControls.filter(c => !c.auto_mapped).length;
    const mappedCount = Array.from(mappedControlIds).filter(id =>
      relevantControls.some(c => c.id === id && !c.auto_mapped)
    ).length;

    return {
      errors,
      warnings,
      isValid: errors.length === 0,
      totalControls: relevantControls.length,
      totalMappableControls,
      mappedCount,
      requiredCount: requiredControls.length,
      autoMappedCount: relevantControls.filter(c => c.auto_mapped).length,
    };
  }, [allControls, allSections, selectedSections, mappings]);

  // Notify parent of validation changes
  React.useEffect(() => {
    if (onValidationChange) {
      onValidationChange(
        validationResult.isValid,
        [...validationResult.errors, ...validationResult.warnings]
      );
    }
  }, [validationResult, onValidationChange]);

  // Handle accordion expansion
  const handleAccordionChange = (panel: string) => (
    event: React.SyntheticEvent,
    isExpanded: boolean
  ) => {
    setExpandedPanel(isExpanded ? panel : false);
  };

  // Handle navigation to fix issues
  const handleFixIssues = (step: number) => {
    if (onNavigateToStep) {
      onNavigateToStep(step);
    }
  };

  // Get mapping for a control
  const getMappingForControl = (controlId: string): ComposerAssetData | undefined => {
    return mappings.find(m => m.controlId === controlId);
  };

  // Render template type icon
  const renderTemplateTypeIcon = () => {
    switch (templateType) {
      case 'global':
        return <PublicIcon color="primary" />;
      case 'personal':
        return <PersonIcon color="action" />;
      default:
        return <DescriptionIcon color="action" />;
    }
  };

  return (
    <Box>
      {/* Validation Status Banner */}
      {validationResult.isValid ? (
        <Alert
          severity="success"
          icon={<CheckCircleIcon />}
          sx={{ mb: 3 }}
        >
          <AlertTitle>Ready to Generate</AlertTitle>
          All required fields are mapped. Your document is ready to be generated.
        </Alert>
      ) : (
        <Alert
          severity="error"
          icon={<ErrorIcon />}
          sx={{ mb: 3 }}
          action={
            validationResult.errors.length > 0 && validationResult.errors[0].step ? (
              <Button
                color="inherit"
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => handleFixIssues(validationResult.errors[0].step!)}
              >
                Fix Issues
              </Button>
            ) : null
          }
        >
          <AlertTitle>
            {validationResult.errors.length} Issue{validationResult.errors.length !== 1 ? 's' : ''} Found
          </AlertTitle>
          Please resolve the following issues before generating your document:
          <List dense sx={{ mt: 1 }}>
            {validationResult.errors.slice(0, 3).map((error, index) => (
              <ListItem key={index} sx={{ py: 0 }}>
                <ListItemText
                  primary={error.message}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
            {validationResult.errors.length > 3 && (
              <ListItem sx={{ py: 0 }}>
                <ListItemText
                  primary={`...and ${validationResult.errors.length - 3} more`}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                />
              </ListItem>
            )}
          </List>
        </Alert>
      )}

      {/* Warnings Banner */}
      {validationResult.warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>
            {validationResult.warnings.length} Warning{validationResult.warnings.length !== 1 ? 's' : ''}
          </AlertTitle>
          {validationResult.warnings.slice(0, 2).map((warning, index) => (
            <Typography key={index} variant="body2">
              {warning.message}
            </Typography>
          ))}
          {validationResult.warnings.length > 2 && (
            <Typography variant="body2" color="text.secondary">
              ...and {validationResult.warnings.length - 2} more
            </Typography>
          )}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Template Summary Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                {renderTemplateTypeIcon()}
                <Typography variant="h6" component="h3">
                  Template
                </Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Name
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {templateName}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Type
                  </Typography>
                  <Chip
                    label={templateType.charAt(0).toUpperCase() + templateType.slice(1)}
                    size="small"
                    color={templateType === 'global' ? 'primary' : 'default'}
                    variant="outlined"
                  />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Total Controls
                  </Typography>
                  <Typography variant="body1">
                    {validationResult.totalControls}
                    {validationResult.autoMappedCount > 0 && (
                      <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        ({validationResult.autoMappedCount} auto-mapped)
                      </Typography>
                    )}
                  </Typography>
                </Box>
              </Stack>
              {onNavigateToStep && (
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => onNavigateToStep(1)}
                  sx={{ mt: 2 }}
                >
                  Change Template
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Sections Summary Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <ViewListIcon color="action" />
                <Typography variant="h6" component="h3">
                  Sections
                </Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              {allSections.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  This template does not have defined sections.
                </Typography>
              ) : (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={`${selectedSections.length} of ${allSections.length} selected`}
                      color={selectedSections.length === allSections.length ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                  <List dense sx={{ maxHeight: 150, overflow: 'auto' }}>
                    {allSections.map((section) => {
                      const isSelected = selectedSections.includes(section.id);
                      return (
                        <ListItem key={section.id} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {isSelected ? (
                              <CheckCircleOutlineIcon color="success" fontSize="small" />
                            ) : (
                              <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={section.name}
                            primaryTypographyProps={{
                              variant: 'body2',
                              color: isSelected ? 'text.primary' : 'text.disabled',
                            }}
                          />
                          {section.required && (
                            <Chip label="Required" size="small" color="warning" variant="outlined" />
                          )}
                        </ListItem>
                      );
                    })}
                  </List>
                </>
              )}
              {onNavigateToStep && allSections.length > 0 && (
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => onNavigateToStep(2)}
                  sx={{ mt: 2 }}
                >
                  Edit Sections
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Assets Summary Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <LinkIcon color="action" />
                <Typography variant="h6" component="h3">
                  Asset Mappings
                </Typography>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Mapping Progress
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip
                      label={`${validationResult.mappedCount} of ${validationResult.totalMappableControls} mapped`}
                      color={
                        validationResult.mappedCount === validationResult.totalMappableControls
                          ? 'success'
                          : validationResult.mappedCount >= validationResult.requiredCount
                          ? 'warning'
                          : 'error'
                      }
                      size="small"
                    />
                  </Stack>
                </Box>
                {validationResult.requiredCount > 0 && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Required Controls
                    </Typography>
                    <Typography variant="body1">
                      {validationResult.requiredCount - validationResult.errors.length} of {validationResult.requiredCount} mapped
                      {validationResult.errors.length > 0 && (
                        <Tooltip title="Some required controls are not mapped">
                          <WarningIcon color="error" fontSize="small" sx={{ ml: 1, verticalAlign: 'middle' }} />
                        </Tooltip>
                      )}
                    </Typography>
                  </Box>
                )}
              </Stack>
              {onNavigateToStep && (
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => onNavigateToStep(3)}
                  sx={{ mt: 2 }}
                >
                  Edit Mappings
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Accordions */}
      <Paper variant="outlined" sx={{ borderRadius: 1 }}>
        {/* Template Details Accordion */}
        <Accordion
          expanded={expandedPanel === 'template'}
          onChange={handleAccordionChange('template')}
          disableGutters
          sx={{
            '&:before': { display: 'none' },
            boxShadow: 'none',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="template-content"
            id="template-header"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <DescriptionIcon color="action" fontSize="small" />
              <Typography variant="subtitle1">Template Details</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Template ID</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{templateId}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary">Deliverable ID</Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{deliverableId}</Typography>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        <Divider />

        {/* Sections Details Accordion */}
        <Accordion
          expanded={expandedPanel === 'sections'}
          onChange={handleAccordionChange('sections')}
          disableGutters
          sx={{
            '&:before': { display: 'none' },
            boxShadow: 'none',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="sections-content"
            id="sections-header"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <ViewListIcon color="action" fontSize="small" />
              <Typography variant="subtitle1">
                Selected Sections ({selectedSections.length})
              </Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            {selectedSections.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No sections selected. All content from the template will be included.
              </Typography>
            ) : (
              <List dense>
                {selectedSections.map((sectionId) => {
                  const section = allSections.find(s => s.id === sectionId);
                  const sectionControls = allControls.filter(c => c.section_id === sectionId);
                  return (
                    <ListItem key={sectionId}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircleIcon color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={section?.name || sectionId}
                        secondary={
                          section?.description ||
                          `${sectionControls.length} control${sectionControls.length !== 1 ? 's' : ''}`
                        }
                      />
                      {section?.required && (
                        <Chip label="Required" size="small" color="warning" variant="outlined" />
                      )}
                    </ListItem>
                  );
                })}
              </List>
            )}
          </AccordionDetails>
        </Accordion>

        <Divider />

        {/* Mappings Details Accordion */}
        <Accordion
          expanded={expandedPanel === 'mappings'}
          onChange={handleAccordionChange('mappings')}
          disableGutters
          sx={{
            '&:before': { display: 'none' },
            boxShadow: 'none',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="mappings-content"
            id="mappings-header"
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <LinkIcon color="action" fontSize="small" />
              <Typography variant="subtitle1">
                Control Mappings ({validationResult.mappedCount}/{validationResult.totalMappableControls})
              </Typography>
              {validationResult.errors.length > 0 && (
                <Chip
                  label={`${validationResult.errors.length} unmapped required`}
                  size="small"
                  color="error"
                />
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            {validationResult.totalMappableControls === 0 ? (
              <Typography variant="body2" color="text.secondary">
                All controls in this template are auto-mapped.
              </Typography>
            ) : (
              <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
                {allControls
                  .filter(control => !control.auto_mapped)
                  .filter(control => {
                    if (!control.section_id) return true;
                    return selectedSections.includes(control.section_id);
                  })
                  .map((control) => {
                    const mapping = getMappingForControl(control.id);
                    const isMapped = !!(mapping?.assetId || mapping?.content);

                    return (
                      <ListItem
                        key={control.id}
                        sx={{
                          bgcolor: !isMapped && control.required ? 'error.50' : 'transparent',
                          borderRadius: 1,
                          mb: 0.5,
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {isMapped ? (
                            <CheckCircleIcon color="success" fontSize="small" />
                          ) : control.required ? (
                            <ErrorIcon color="error" fontSize="small" />
                          ) : (
                            <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2">{control.name}</Typography>
                              <Chip
                                label={control.type.replace('_', ' ')}
                                size="small"
                                variant="outlined"
                                sx={{ height: 20 }}
                              />
                            </Stack>
                          }
                          secondary={
                            isMapped
                              ? mapping?.assetId
                                ? `Mapped to asset: ${mapping.assetId}`
                                : 'Has direct content'
                              : control.required
                              ? 'Required - Not mapped'
                              : 'Optional - Not mapped'
                          }
                        />
                        {control.required && (
                          <Chip
                            label="Required"
                            size="small"
                            color={isMapped ? 'success' : 'error'}
                            variant="outlined"
                          />
                        )}
                      </ListItem>
                    );
                  })}
              </List>
            )}
          </AccordionDetails>
        </Accordion>
      </Paper>
    </Box>
  );
};

export default PreviewStep;
